import { Button } from '@bambi/component-library/src/components/Button';
import { ContextForm } from '@bambi/component-library/src/components/forms/context/ContextForm';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  useTripRequestEmbedCreateMutation,
  useTripRequestEmbedPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { ContextFormFieldSwitch } from 'src/common/FormField/v2/context/ContextFormFieldSwitch';
import { FormSubmitButton } from 'src/common/FormSubmitButton';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatRTKQueryError } from 'src/common/util/formatRTKQueryError';

import { settingSlice } from '../setting.slice';

export function TripRequestEmbedFormModal() {
  const dispatch = useAppDispatch();
  const { isOpen, tripRequestEmbed } = useAppSelector(
    (state) => state.setting.tripRequestEmbedModal
  );

  return (
    <Modal
      open={isOpen}
      setOpen={() =>
        dispatch(settingSlice.actions.closeTripRequestEmbedModal())
      }
      onClose={() =>
        dispatch(settingSlice.actions.closeTripRequestEmbedModal())
      }
      onInteractOutside={(e) => {
        e.preventDefault();
      }}
      title={
        tripRequestEmbed ? 'Edit Trip Request Embed' : 'Add Trip Request Embed'
      }
    >
      <TripRequestEmbedForm />
    </Modal>
  );

  function TripRequestEmbedForm() {
    const dispatch = useAppDispatch();
    const { tripRequestEmbed } = useAppSelector(
      (state) => state.setting.tripRequestEmbedModal
    );
    const [
      createTripRequestEmbed,
      {
        isLoading: isCreatingTripRequestEmbed,
        error: createTripRequestEmbedError,
      },
    ] = useTripRequestEmbedCreateMutation();
    const [
      updateTripRequestEmbed,
      {
        isLoading: isUpdatingTripRequestEmbed,
        error: updateTripRequestEmbedError,
      },
    ] = useTripRequestEmbedPartialUpdateMutation();

    return (
      <ContextForm
        defaultValues={{
          label: tripRequestEmbed?.label ?? '',
          domain: tripRequestEmbed?.domain ?? '',
          description: tripRequestEmbed?.description ?? '',
          is_active: tripRequestEmbed?.is_active ?? true,
        }}
        onSubmit={async (values) => {
          if (tripRequestEmbed) {
            try {
              await updateTripRequestEmbed({
                id: tripRequestEmbed.id,
                patchedTripRequestEmbed: values,
              }).unwrap();
              dispatch(
                show({
                  title: `${tripRequestEmbed.label} updated successfully`,
                  type: 'success',
                })
              );
              dispatch(settingSlice.actions.closeTripRequestEmbedModal());
            } catch {
              dispatch(
                show({
                  title: 'Error updating trip request embed',
                  description:
                    'There was an error updating the trip request embed.',
                  type: 'error',
                })
              );
            }
          } else {
            try {
              await createTripRequestEmbed({
                tripRequestEmbed: values,
              }).unwrap();
              dispatch(
                show({
                  title: `${values.label} created successfully`,
                  type: 'success',
                })
              );
              dispatch(settingSlice.actions.closeTripRequestEmbedModal());
            } catch {
              dispatch(
                show({
                  title: 'Error creating trip request embed',
                  description:
                    'There was an error creating the trip request embed.',
                  type: 'error',
                })
              );
            }
          }
        }}
      >
        <div
          className="flex min-w-[350px] flex-col gap-4"
          data-testid="trip-request-embed-form-container"
        >
          <h2 className="text-lg font-medium">
            {tripRequestEmbed
              ? 'Edit Trip Request Embed'
              : 'Add Trip Request Embed'}
          </h2>
          {createTripRequestEmbedError && (
            <FormErrorMessage>
              {formatRTKQueryError(createTripRequestEmbedError)}
            </FormErrorMessage>
          )}
          {updateTripRequestEmbedError && (
            <FormErrorMessage>
              {formatRTKQueryError(updateTripRequestEmbedError)}
            </FormErrorMessage>
          )}
          <ContextFormFieldText
            fieldPath="label"
            label="Label"
            rules={{ required: 'Label is required' }}
          />
          <ContextFormFieldText
            fieldPath="domain"
            label="Domain"
            rules={{
              required: 'Domain is required',
              validate: (value) => {
                if (!value) {
                  return 'Domain is required';
                }
                if (process.env.NODE_ENV === 'development') {
                  return value.match(/^localhost(:\d+)?$/)
                    ? true
                    : 'Domain is invalid';
                }
                return value.match(/^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/)
                  ? true
                  : 'Domain is invalid';
              },
            }}
          />
          <ContextFormFieldTextArea
            fieldPath="description"
            label="Description"
          />
          <ContextFormFieldSwitch fieldPath="is_active" label="Is Active" />
          <div className="flex flex-row items-center justify-end gap-4">
            <Button
              variant="secondary"
              onClick={() => {
                dispatch(settingSlice.actions.closeTripRequestEmbedModal());
              }}
            >
              Cancel
            </Button>
            <FormSubmitButton
              isSubmitting={
                isCreatingTripRequestEmbed || isUpdatingTripRequestEmbed
              }
              buttonName="Save"
            />
          </div>
        </div>
      </ContextForm>
    );
  }
}
